import type { MongoAbility, RawRuleOf } from "@casl/ability";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import type { TFetchStatus } from "./types";

type AppAbility = MongoAbility<[string, string]>;
export type Permission = RawRuleOf<AppAbility>;

interface Action {
    name: string;
    description: string;
}

interface Subject {
    name: string;
    category: string;
    description: string;
    actions: string[];
}

export interface RoleListItem {
    id?: number;
    name: string;
    description?: string;
    isCustom: boolean;
    userCount?: number;
    brands?: string[];
    contractTypes?: string[];
}

export interface RoleResponse extends RoleListItem {
    permissions?: Permission[];
}

interface PermissionsState {
    actions: Action[];
    subjects: Subject[];
    roles: RoleListItem[];
    allRolePermissions: Record<string, Permission[]>;
    currentRolePermissions: Permission[] | null;
    fetchStatus: TFetchStatus;
    actionsFetchStatus: TFetchStatus;
    subjectsFetchStatus: TFetchStatus;
    rolesFetchStatus: TFetchStatus;
    createRoleStatus: TFetchStatus;
    updateRoleStatus: TFetchStatus;
}

const initialState: PermissionsState = {
    actions: [],
    subjects: [],
    roles: [],
    allRolePermissions: {},
    currentRolePermissions: null,
    fetchStatus: "idle",
    actionsFetchStatus: "idle",
    subjectsFetchStatus: "idle",
    rolesFetchStatus: "idle",
    createRoleStatus: "idle",
    updateRoleStatus: "idle",
};

// Fetch available permission actions
export const fetchAvailableActions = createAsyncThunk("permissions/fetchActions", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: Action[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/company/permissions/actions",
        );
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Fetch available permission subjects
export const fetchAvailableSubjects = createAsyncThunk("permissions/fetchSubjects", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: Subject[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/company/permissions/subjects",
        );
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Fetch all roles (predefined and custom) for a given company
export const fetchRoles = createAsyncThunk("permissions/fetchRoles", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: RoleListItem[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/company/roles",
        );
        console.log("fetchRoles response:", response.data);
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error?.response?.data || { message: error?.message || "Failed to fetch roles" });
    }
});

// Create a new custom role with permissions
export const createRole = createAsyncThunk(
    "permissions/createRole",
    async (
        { name, description, permissions }: { name: string; description?: string; permissions?: Permission[] },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/company/roles", {
                name,
                description,
                permissions,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || { message: error?.message || "Failed to create role" });
        }
    },
);

// Fetch custom role by ID
export const fetchRolePermissions = createAsyncThunk(
    "permissions/fetchRole",
    async (roleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get<{
                data: { id: number; name: string; description: string; permissions: Permission[] };
            }>(import.meta.env.REACT_APP_API_ENDPOINT + `/company/roles/${roleId}`);
            return response.data.data.permissions;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Fetch predefined/hardcoded role
export const fetchSystemRolePermissions = createAsyncThunk(
    "permissions/fetchSystemRole",
    async (roleName: string, { rejectWithValue }) => {
        try {
            const response = await axios.get<{
                data: { id: null; name: string; description: string | null; permissions: Permission[] };
            }>(import.meta.env.REACT_APP_API_ENDPOINT + `/company/roles/system/${roleName}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateRolePermissions = createAsyncThunk(
    "permissions/updateRole",
    async (
        {
            roleId,
            name,
            description,
            permissions,
        }: { roleId: number; name?: string; description?: string; permissions?: Permission[] },
        { rejectWithValue },
    ) => {
        try {
            const payload: any = {};
            if (name !== undefined) payload.name = name;
            if (description !== undefined) payload.description = description;
            if (permissions !== undefined) payload.permissions = permissions;

            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/company/roles/${roleId}`,
                payload,
            );
            return { roleId, name, description, permissions };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Update system/predefined role permissions (creates a company-specific override)
export const updateSystemRolePermissions = createAsyncThunk(
    "permissions/updateSystemRole",
    async (
        {
            roleName,
            name,
            description,
            permissions,
        }: { roleName: string; name?: string; description?: string; permissions?: Permission[] },
        { rejectWithValue },
    ) => {
        try {
            const payload: any = {};
            if (name !== undefined) payload.name = name;
            if (description !== undefined) payload.description = description;
            if (permissions !== undefined) payload.permissions = permissions;

            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/company/roles/system/${roleName}`,
                payload,
            );
            return { roleName, name, description, permissions };
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { message: error?.message || "Failed to update system role" },
            );
        }
    },
);

export const deleteRolePermissions = createAsyncThunk(
    "permissions/deleteRole",
    async (roleId: number, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/company/roles/${roleId}`);
            return roleId;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

const permissionsSlice = createSlice({
    name: "permissions",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAvailableActions.pending, (state) => {
                state.actionsFetchStatus = "pending";
            })
            .addCase(fetchAvailableActions.fulfilled, (state, action) => {
                state.actions = action.payload;
                state.actionsFetchStatus = "succeeded";
            })
            .addCase(fetchAvailableActions.rejected, (state) => {
                state.actionsFetchStatus = "failed";
            })
            .addCase(fetchAvailableSubjects.pending, (state) => {
                state.subjectsFetchStatus = "pending";
            })
            .addCase(fetchAvailableSubjects.fulfilled, (state, action) => {
                state.subjects = action.payload;
                state.subjectsFetchStatus = "succeeded";
            })
            .addCase(fetchAvailableSubjects.rejected, (state) => {
                state.subjectsFetchStatus = "failed";
            })
            .addCase(fetchRoles.pending, (state) => {
                state.rolesFetchStatus = "pending";
            })
            .addCase(fetchRoles.fulfilled, (state, action) => {
                state.roles = action.payload;
                state.rolesFetchStatus = "succeeded";
            })
            .addCase(fetchRoles.rejected, (state) => {
                state.rolesFetchStatus = "failed";
            })
            .addCase(fetchRolePermissions.fulfilled, (state, action) => {
                state.currentRolePermissions = action.payload;
            })
            .addCase(createRole.pending, (state) => {
                state.createRoleStatus = "pending";
            })
            .addCase(createRole.fulfilled, (state, action) => {
                // Add newly created role to allRolePermissions
                state.allRolePermissions[action.payload.name] = action.payload.permissions || [];
                state.createRoleStatus = "succeeded";
            })
            .addCase(createRole.rejected, (state) => {
                state.createRoleStatus = "failed";
            })
            .addCase(updateRolePermissions.pending, (state) => {
                state.updateRoleStatus = "pending";
            })
            .addCase(updateRolePermissions.fulfilled, (state, action) => {
                if (action.payload.name && action.payload.permissions) {
                    state.allRolePermissions[action.payload.name] = action.payload.permissions;
                }
                state.updateRoleStatus = "succeeded";
            })
            .addCase(updateRolePermissions.rejected, (state) => {
                state.updateRoleStatus = "failed";
            })
            .addCase(updateSystemRolePermissions.pending, (state) => {
                state.updateRoleStatus = "pending";
            })
            .addCase(updateSystemRolePermissions.fulfilled, (state, action) => {
                if (action.payload.roleName && action.payload.permissions) {
                    state.allRolePermissions[action.payload.roleName] = action.payload.permissions;
                }
                state.updateRoleStatus = "succeeded";
            })
            .addCase(updateSystemRolePermissions.rejected, (state) => {
                state.updateRoleStatus = "failed";
            })
            .addCase(deleteRolePermissions.fulfilled, (state, action) => {
                delete state.allRolePermissions[action.payload];
            });
    },
});

export default permissionsSlice.reducer;
